import { useState, useEffect, useCallback, useRef } from 'react';
import {
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  collection,
  onSnapshot,
  setDoc,
  addDoc
} from 'firebase/firestore';
import { db } from '../firebase';
import toast from 'react-hot-toast';

/**
 * Custom hook to manage all app data (shows, inventory, expenses, contacts, polls, documents)
 */
export function useAppData(user, currentGroup, isDemoMode, inventoryMigrated, setInventoryMigrated) {
  // Data state
  const [shows, setShows] = useState({});
  const [generalExpenses, setGeneralExpenses] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [polls, setPolls] = useState([]);
  const [salesLog, setSalesLog] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);

  // Ref to track local changes timing
  const lastLocalChangeTimeRef = useRef(0);

  // Reset all data
  const resetAllData = useCallback(() => {
    setShows({});
    setGeneralExpenses([]);
    setInventory([]);
    setDocuments([]);
    setContacts([]);
    setPolls([]);
    setSalesLog([]);
    setDataLoaded(false);
    setHasLocalChanges(false);
  }, []);

  // Handle remote data changes
  const handleDataChange = useCallback((data) => {
        // DEBUG: Log incoming data from Firebase
    console.log('🔥 [handleDataChange] Raw data from Firebase:');
    Object.entries(data.shows || {}).forEach(([dateKey, dayShows]) => {
      dayShows.forEach(show => {
        if (show.cashMerch && parseFloat(show.cashMerch) > 0) {
          console.log('🔥 [handleDataChange] Show with cashMerch:', {
            dateKey,
            showId: show.id,
            title: show.title || show.venue,
            cashMerch: show.cashMerch,
            merchSales: show.merchSales,
            merchSalesLength: show.merchSales?.length || 0,
            merchSalesType: typeof show.merchSales
          });
        }
      });
    });

    if (isDemoMode) return;
    
    const timeSinceChange = Date.now() - lastLocalChangeTimeRef.current;
    if (timeSinceChange < 2000) {
      console.log('Ignoring remote update - recent local change');
      return;
    }

    if (!hasLocalChanges) {
      setShows(data.shows || {});
      setGeneralExpenses(data.generalExpenses || data.expenses || []);
      setContacts(data.contacts || []);
      setPolls(data.polls || []);

      if (data.inventoryMigrated) {
        setInventoryMigrated(true);
      } else {
        setInventoryMigrated(false);
        setInventory(data.inventory || []);
      }

      setDataLoaded(true);
    }
  }, [hasLocalChanges, isDemoMode, setInventoryMigrated]);

  // =====================================================
  // HELPER: Find show by ID across all dates
  // =====================================================
  const findShowById = useCallback((showId) => {
    for (const [dateKey, dayShows] of Object.entries(shows)) {
      if (!Array.isArray(dayShows)) continue;
      const show = dayShows.find(s => s.id === showId);
      if (show) {
        return { dateKey, show };
      }
    }
    return null;
  }, [shows]);

  // =====================================================
  // SHOW HANDLERS
  // =====================================================
  const updateShow = useCallback((dateKey, showId, updatedData) => {
       // DEBUG: Log show update
    console.log('📝 [updateShow] Updating show:', {
      dateKey,
      showId,
      updatedDataKeys: Object.keys(updatedData),
      hasMerchSales: 'merchSales' in updatedData,
      merchSalesValue: updatedData.merchSales,
      merchSalesLength: updatedData.merchSales?.length || 0,
      cashMerch: updatedData.cashMerch
    });
    // First, try to find the show at the given dateKey
    let foundDateKey = dateKey;
    let foundShow = null;

    if (shows[dateKey] && Array.isArray(shows[dateKey])) {
      foundShow = shows[dateKey].find(s => s.id === showId);
      if (foundShow) {
        foundDateKey = dateKey;
      }
    }

    // If not found at given dateKey, search all dates (for multi-day events)
    if (!foundShow) {
      const result = findShowById(showId);
      if (result) {
        foundDateKey = result.dateKey;
        foundShow = result.show;
      }
    }

    if (!foundShow) {
      console.error(`Show not found: ${showId}`);
      toast.error('Error updating event');
      return;
    }
    
    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);

    // Determine the new storage key
    // For multi-day events, store on startDate; otherwise use original dateKey
    const newStorageKey = updatedData.isMultiDay && updatedData.startDate
      ? updatedData.startDate
      : foundDateKey;

    // Check if we need to move the show to a different date
    if (newStorageKey !== foundDateKey) {
      // Remove from old date and add to new date
      setShows(prev => {
        const newShows = { ...prev };
        
        // Remove from old date
        if (newShows[foundDateKey]) {
          newShows[foundDateKey] = newShows[foundDateKey].filter(show => show.id !== showId);
          // Clean up empty arrays
          if (newShows[foundDateKey].length === 0) {
            delete newShows[foundDateKey];
          }
        }
        
        // Add to new date
        const updatedShow = { ...foundShow, ...updatedData };
        newShows[newStorageKey] = [
          ...(newShows[newStorageKey] || []),
          updatedShow
        ];
        
        return newShows;
      });
    } else {
      // Update in place
      setShows(prev => ({
        ...prev,
        [foundDateKey]: prev[foundDateKey].map(show =>
          show.id === showId ? { ...show, ...updatedData } : show
        )
      }));
    }

    toast.success('Event updated successfully!');
  }, [shows, findShowById]);

  const deleteShow = useCallback((dateKey, showId) => {
  console.log('🗑️ [deleteShow] Called with:', { dateKey, showId });
  
  lastLocalChangeTimeRef.current = Date.now();
  setHasLocalChanges(true);

  setShows(prev => {
    console.log('🗑️ [deleteShow] Current shows keys:', Object.keys(prev));
    console.log('🗑️ [deleteShow] Looking for show at dateKey:', dateKey);
    
    // First check if the show exists at the provided dateKey
    let actualDateKey = dateKey;
    
    if (!prev[dateKey]?.find(s => s.id === showId)) {
      console.log('🗑️ [deleteShow] Show NOT found at provided dateKey, searching all dates...');
      // Search all dates for the show (for multi-day events)
      for (const [key, dayShows] of Object.entries(prev)) {
        if (!Array.isArray(dayShows)) continue;
        const found = dayShows.find(s => s.id === showId);
        if (found) {
          actualDateKey = key;
          console.log('🗑️ [deleteShow] Found show at:', actualDateKey);
          break;
        }
      }
    } else {
      console.log('🗑️ [deleteShow] Show found at provided dateKey');
    }

    const newShows = { ...prev };
    
    if (newShows[actualDateKey]) {
      newShows[actualDateKey] = newShows[actualDateKey].filter(show => show.id !== showId);
      
      // Clean up empty arrays
      if (newShows[actualDateKey].length === 0) {
        delete newShows[actualDateKey];
      }
    }
    
    return newShows;
  });
}, []);  

  const addShow = useCallback((dateKey, showData) => {
    lastLocalChangeTimeRef.current = Date.now();

    // For multi-day events, store on the start date
    const storageKey = showData.isMultiDay && showData.startDate 
      ? showData.startDate 
      : dateKey;

    // Check if recurring
    if (showData.recurrence?.pattern && showData.recurrence.pattern !== 'none') {
      let count = 0;
      const startDate = new Date(dateKey);
      const recurrence = showData.recurrence;

      const dates = [];
      let current = new Date(startDate);
      const maxEvents = 12;
      const endDate = recurrence.endDate ? new Date(recurrence.endDate) : null;

      if (recurrence.pattern === 'weekly' && recurrence.daysOfWeek?.length > 0) {
        const weekStart = new Date(current);
        weekStart.setDate(current.getDate() - current.getDay());

        let weeksProcessed = 0;
        const maxWeeks = Math.ceil(maxEvents / recurrence.daysOfWeek.length) + 1;

        while (dates.length < maxEvents && weeksProcessed < maxWeeks) {
          for (const dayOfWeek of recurrence.daysOfWeek.sort((a, b) => a - b)) {
            const date = new Date(weekStart);
            date.setDate(weekStart.getDate() + dayOfWeek);

            if (date < startDate) continue;
            if (endDate && date > endDate) break;
            if (dates.length >= maxEvents) break;

            dates.push(new Date(date));
          }

          weekStart.setDate(weekStart.getDate() + 7);
          weeksProcessed++;
        }
      } else {
        while (dates.length < maxEvents) {
          if (endDate && current > endDate) break;
          dates.push(new Date(current));

          if (recurrence.pattern === 'daily') current.setDate(current.getDate() + 1);
          else if (recurrence.pattern === 'weekly') current.setDate(current.getDate() + 7);
          else if (recurrence.pattern === 'biweekly') current.setDate(current.getDate() + 14);
          else if (recurrence.pattern === 'monthly') current.setMonth(current.getMonth() + 1);
          else break;
        }
      }

      const seriesId = `series-${Date.now()}`;

      setShows(prev => {
        const newShows = { ...prev };
        dates.forEach((date, index) => {
          const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
          const newShow = {
            id: Date.now() + index,
            ...showData,
            recurrence: undefined,
            seriesId,
            seriesIndex: index,
            seriesTotal: dates.length,
            isRecurring: true,
            completed: false
          };
          newShows[key] = [...(newShows[key] || []), newShow];
          count++;
        });
        return newShows;
      });

      setHasLocalChanges(true);
      toast.success(`Created ${count} ${showData.eventType || 'event'}${count > 1 ? 's' : ''}`);
      return;
    }

    // Single event (non-recurring)
    const newShow = {
      id: Date.now(),
      ...showData,
      completed: false
    };

    setHasLocalChanges(true);
    setShows(prev => ({
      ...prev,
      [storageKey]: [...(prev[storageKey] || []), newShow]
    }));
  }, []);

  const toggleShow = useCallback((dateKey, showId) => {
    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);

    // Find the actual storage location for the show
    let actualDateKey = dateKey;
    
    if (!shows[dateKey]?.find(s => s.id === showId)) {
      const result = findShowById(showId);
      if (result) {
        actualDateKey = result.dateKey;
      }
    }

    setShows(prev => ({
      ...prev,
      [actualDateKey]: (prev[actualDateKey] || []).map(show =>
        show.id === showId ? { ...show, completed: !show.completed } : show
      )
    }));
  }, [shows, findShowById]);

  // =====================================================
  // EXPENSE HANDLERS
  // =====================================================
  const addGeneralExpense = useCallback((description, amount, date, category) => {
    const newExpense = {
      id: Date.now(),
      category: category,
      description: description,
      amount: amount,
      date: date
    };
    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setGeneralExpenses(prev => [...prev, newExpense]);
  }, []);

  const updateGeneralExpense = useCallback((id, description, amount, date, category) => {
    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setGeneralExpenses(prev => prev.map(exp =>
      exp.id === id ? { ...exp, category, description, amount, date } : exp
    ));
  }, []);

  const deleteGeneralExpense = useCallback((id) => {
    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setGeneralExpenses(prev => prev.filter(exp => exp.id !== id));
  }, []);

  // =====================================================
  // INVENTORY HANDLERS
  // =====================================================
  const addInventoryItem = useCallback(async (item, size, quantity, msrp, sellPrice, sku, reorderPoint, imageUrl, notes, hasSizes, name) => {
    const newItem = {
      id: Date.now(),
      name: name || '',
      item: item,
      size: size,
      quantity: quantity,
      msrp: msrp,
      cost: msrp,
      sellPrice: sellPrice,
      sku: sku || '',
      reorderPoint: reorderPoint || '5',
      imageUrl: imageUrl || '',
      notes: notes || '',
      hasSizes: hasSizes || false,
      createdAt: new Date().toISOString()
    };

    if (inventoryMigrated && currentGroup?.id) {
      try {
        const inventoryRef = collection(db, 'groups', currentGroup.id, 'inventory');
        await addDoc(inventoryRef, newItem);
      } catch (error) {
        console.error('Error adding inventory item:', error);
        toast.error('Failed to add item');
      }
    } else {
      setHasLocalChanges(true);
      setInventory(prev => [...prev, newItem]);
    }
  }, [inventoryMigrated, currentGroup?.id]);

  const updateInventoryItem = useCallback(async (id, item, size, quantity, cost, sellPrice, sku, reorderPoint, imageUrl, notes, hasSizes = false, name) => {
    const updatedData = {
      name: name || '',
      item,
      size,
      quantity,
      cost: cost,
      msrp: cost,
      sellPrice,
      sku: sku || '',
      reorderPoint: reorderPoint || '5',
      imageUrl: imageUrl || '',
      notes: notes || '',
      hasSizes: hasSizes,
      updatedAt: new Date().toISOString()
    };

    if (inventoryMigrated && currentGroup?.id) {
      try {
        const itemRef = doc(db, 'groups', currentGroup.id, 'inventory', String(id));
        await updateDoc(itemRef, updatedData);
      } catch (error) {
        console.error('Error updating inventory item:', error);
        toast.error('Failed to update item');
      }
    } else {
      setHasLocalChanges(true);
      setInventory(prev => prev.map(invItem =>
        invItem.id === id ? { ...invItem, ...updatedData } : invItem
      ));
    }
  }, [inventoryMigrated, currentGroup?.id]);

  const deleteInventoryItem = useCallback(async (id) => {
    if (inventoryMigrated && currentGroup?.id) {
      try {
        const itemRef = doc(db, 'groups', currentGroup.id, 'inventory', String(id));
        await deleteDoc(itemRef);
      } catch (error) {
        console.error('Error deleting inventory item:', error);
        toast.error('Failed to delete item');
      }
    } else {
      setHasLocalChanges(true);
      setInventory(prev => prev.filter(item => item.id !== id));
    }
  }, [inventoryMigrated, currentGroup?.id]);

  // Record merch sale tied to a show
  const handleRecordMerchSale = useCallback((saleData) => {
    if (!saleData.showId || !saleData.showDateKey) return;
    lastLocalChangeTimeRef.current = Date.now();
    const dateKey = saleData.showDateKey;
    const showId = saleData.showId;

    // Find the actual storage location for the show
    let actualDateKey = dateKey;
    const result = findShowById(showId);
    if (result) {
      actualDateKey = result.dateKey;
    }

    setShows(prev => {
      if (!prev[actualDateKey]) return prev;
      
      return {
        ...prev,
        [actualDateKey]: prev[actualDateKey].map(show => {
          if (show.id === showId) {
            const existingSales = show.merchSales || [];
            const newSale = {
              id: Date.now(),
              itemName: saleData.itemName,
              size: saleData.size,
              quantity: saleData.quantity,
              price: saleData.sellPrice,
              total: saleData.total,
              timestamp: saleData.timestamp
            };

            return {
              ...show,
              merchSales: [...existingSales, newSale],
              cashMerch: (parseFloat(show.cashMerch || 0) + saleData.total).toString()
            };
          }
          return show;
        })
      };
    });
    setHasLocalChanges(true);
  }, [findShowById]);

  // =====================================================
  // CONTACT HANDLERS
  // =====================================================
  const addContact = useCallback((contactData) => {
    lastLocalChangeTimeRef.current = Date.now();
    const newContact = {
      id: Date.now(),
      ...contactData
    };
    setHasLocalChanges(true);
    setContacts(prev => [...prev, newContact]);
  }, []);

  const updateContact = useCallback((id, contactData) => {
    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setContacts(prev => prev.map(contact =>
      contact.id === id ? { ...contact, ...contactData } : contact
    ));
  }, []);

  const deleteContact = useCallback((id) => {
    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setContacts(prev => prev.filter(contact => contact.id !== id));
  }, []);

  // =====================================================
  // POLL HANDLERS
  // =====================================================
  const handleAddPoll = useCallback((newPoll) => {
    if (!currentGroup && !isDemoMode) return;

    const pollWithCreator = {
      ...newPoll,
      createdBy: user?.uid,
    };

    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setPolls(prev => [pollWithCreator, ...prev]);
    toast.success('Poll created!');
  }, [currentGroup, isDemoMode, user?.uid]);

  const handleVotePoll = useCallback((pollId, optionId) => {
    if (!currentGroup && !isDemoMode) return;
    if (!user) return;

    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setPolls(prev => prev.map(poll => {
      if (poll.id !== pollId) return poll;
      return {
        ...poll,
        options: poll.options.map(opt => {
          const votesWithoutUser = opt.votes?.filter(uid => uid !== user.uid) || [];
          if (opt.id === optionId) {
            return { ...opt, votes: [...votesWithoutUser, user.uid] };
          }
          return { ...opt, votes: votesWithoutUser };
        })
      };
    }));
    toast.success('Vote recorded!');
  }, [currentGroup, isDemoMode, user]);

  const handleClosePoll = useCallback((pollId) => {
    if (!currentGroup && !isDemoMode) return;

    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setPolls(prev => prev.map(poll =>
      poll.id === pollId ? { ...poll, status: 'closed' } : poll
    ));
    toast.success('Poll closed');
  }, [currentGroup, isDemoMode]);

  const handleDeletePoll = useCallback((pollId) => {
    if (!currentGroup && !isDemoMode) return;

    lastLocalChangeTimeRef.current = Date.now();
    setHasLocalChanges(true);
    setPolls(prev => prev.filter(poll => poll.id !== pollId));
    toast.success('Poll deleted');
  }, [currentGroup, isDemoMode]);

  // =====================================================
  // DOCUMENT HANDLERS (Firestore only - no Storage needed)
  // =====================================================
  const handleAddDocument = useCallback(async (newDoc) => {
    if (!currentGroup && !isDemoMode) return;

    // For demo mode, just add to local state
    if (isDemoMode) {
      setDocuments(prev => [newDoc, ...prev]);
      return;
    }

    // Optimistically update local state
    setDocuments(prev => [newDoc, ...prev]);

    try {
      // Save to Firestore
      const docRef = doc(db, 'groups', currentGroup.id, 'data', 'documents');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentDocs = docSnap.data().items || [];
        await updateDoc(docRef, { items: [newDoc, ...currentDocs] });
      } else {
        await setDoc(docRef, { items: [newDoc] });
      }
    } catch (error) {
      console.error('Error adding document:', error);
      toast.error(`Failed to upload document: ${error.message}`);
      // Revert optimistic update on error
      setDocuments(prev => prev.filter(d => d.id !== newDoc.id));
    }
  }, [currentGroup, isDemoMode]);

  const handleUpdateDocument = useCallback(async (docId, updates) => {
    if (!currentGroup && !isDemoMode) return;

    // Optimistically update local state
    setDocuments(prev => prev.map(d =>
      d.id === docId ? { ...d, ...updates } : d
    ));

    if (isDemoMode) return;

    try {
      const docRef = doc(db, 'groups', currentGroup.id, 'data', 'documents');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentDocs = docSnap.data().items || [];
        const updatedDocs = currentDocs.map(d =>
          d.id === docId ? { ...d, ...updates } : d
        );
        await updateDoc(docRef, { items: updatedDocs });
      }
    } catch (error) {
      console.error('Error updating document:', error);
      toast.error('Failed to update document');
    }
  }, [currentGroup, isDemoMode]);

  const handleDeleteDocument = useCallback(async (docId) => {
    if (!currentGroup && !isDemoMode) return;

    // Store for potential rollback
    const deletedDoc = documents.find(d => d.id === docId);
    
    // Optimistically update local state
    setDocuments(prev => prev.filter(d => d.id !== docId));

    if (isDemoMode) return;

    try {
      // Delete from Firestore
      const docRef = doc(db, 'groups', currentGroup.id, 'data', 'documents');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const currentDocs = docSnap.data().items || [];
        const updatedDocs = currentDocs.filter(d => d.id !== docId);
        await updateDoc(docRef, { items: updatedDocs });
      }
      toast.success('Document deleted');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
      // Revert on error
      if (deletedDoc) {
        setDocuments(prev => [...prev, deletedDoc]);
      }
    }
  }, [currentGroup, isDemoMode, documents]);

  // =====================================================
  // SALES LOG HANDLERS
  // =====================================================
  const handleAddSaleLog = useCallback(async (saleLogEntry) => {
    if (!user?.uid) return;

    try {
      const salesLogRef = doc(db, 'users', user.uid, 'salesLog', saleLogEntry.id);
      await setDoc(salesLogRef, {
        ...saleLogEntry,
        createdAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error saving sale log:', error);
      toast.error('Failed to save sales session');
    }
  }, [user?.uid]);

  // Firebase listener for sales log
  useEffect(() => {
    if (!user?.uid) {
      setSalesLog([]);
      return;
    }

    const salesLogRef = collection(db, 'users', user.uid, 'salesLog');

    const unsubscribe = onSnapshot(salesLogRef, (snapshot) => {
      const logs = [];
      snapshot.forEach(doc => {
        logs.push({ id: doc.id, ...doc.data() });
      });
      logs.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      setSalesLog(logs);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  // Real-time listener for inventory collection
  useEffect(() => {
    if (!currentGroup?.id || !inventoryMigrated) return;

    const inventoryRef = collection(db, 'groups', currentGroup.id, 'inventory');

    const unsubscribe = onSnapshot(inventoryRef, (snapshot) => {
      const items = [];
      snapshot.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() });
      });
      setInventory(items);
    }, (error) => {
      console.error('Error listening to inventory collection:', error);
    });

    return () => unsubscribe();
  }, [currentGroup?.id, inventoryMigrated]);

  // Real-time listener for documents
  useEffect(() => {
    if (!currentGroup?.id || isDemoMode) return;

    const docRef = doc(db, 'groups', currentGroup.id, 'data', 'documents');

    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setDocuments(data.items || []);
      } else {
        setDocuments([]);
      }
    }, (error) => {
      console.error('Error listening to documents:', error);
    });

    return () => unsubscribe();
  }, [currentGroup?.id, isDemoMode]);

  // =====================================================
  // HELPER FUNCTIONS
  // =====================================================
  const getAllShowsData = useCallback(() => {
    const allShows = [];
    Object.keys(shows).forEach(dateKey => {
      const [year, month, day] = dateKey.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      shows[dateKey].forEach(show => {
        allShows.push({
          ...show,
          date: date,
          dateKey: dateKey
        });
      });
    });
    return allShows.sort((a, b) => a.date - b.date);
  }, [shows]);

  const getUpcomingShows = useCallback(() => {
    const allShows = getAllShowsData();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return allShows.filter(show => {
      // For multi-day events, check if the end date is in the future
      if (show.isMultiDay && show.endDate) {
        const endDate = new Date(show.endDate);
        endDate.setHours(23, 59, 59, 999);
        return endDate >= today;
      }
      // For single-day events, check the event date
      return show.date >= today;
    });
  }, [getAllShowsData]);

  const getTotalInventoryValue = useCallback(() => {
    return inventory.reduce((sum, item) => {
      if (item.hasSizes && item.size && typeof item.size === 'string' && item.size.startsWith('[')) {
        try {
          const sizes = JSON.parse(item.size);
          const totalQty = sizes.reduce((s, sz) => s + (parseInt(sz.quantity) || 0), 0);
          return sum + (totalQty * parseFloat(item.sellPrice || 0));
        } catch (e) {
          return sum;
        }
      }
      return sum + (parseInt(item.quantity || 0) * parseFloat(item.sellPrice || 0));
    }, 0);
  }, [inventory]);

  const getUnvotedPollsCount = useCallback(() => {
    if (!polls || !user) return 0;
    return polls.filter(poll => {
      if (poll.status !== 'active') return false;
      if (poll.expiresAt && new Date(poll.expiresAt) < new Date()) return false;
      const hasVoted = poll.options?.some(opt => opt.votes?.includes(user.uid));
      return !hasVoted;
    }).length;
  }, [polls, user]);

  // Set demo data
  const setDemoData = useCallback((demoData) => {
    setShows(demoData.shows || {});
    setInventory(demoData.inventory || []);
    setContacts(demoData.contacts || []);
    setPolls(demoData.polls || []);
    setGeneralExpenses(demoData.generalExpenses || []);
    setDocuments(demoData.documents || []);
    setDataLoaded(true);
  }, []);

  return {
    // State
    shows,
    setShows,
    generalExpenses,
    inventory,
    setInventory,
    documents,
    contacts,
    setContacts,
    polls,
    setPolls,
    salesLog,
    setSalesLog,
    dataLoaded,
    setDataLoaded,
    hasLocalChanges,
    setHasLocalChanges,

    // Data change handler
    handleDataChange,
    resetAllData,
    setDemoData,

    // Show handlers
    addShow,
    updateShow,
    deleteShow,
    toggleShow,
    findShowById, // Exported for use in other components if needed

    // Expense handlers
    addGeneralExpense,
    updateGeneralExpense,
    deleteGeneralExpense,

    // Inventory handlers
    addInventoryItem,
    updateInventoryItem,
    deleteInventoryItem,
    handleRecordMerchSale,

    // Contact handlers
    addContact,
    updateContact,
    deleteContact,

    // Poll handlers
    handleAddPoll,
    handleVotePoll,
    handleClosePoll,
    handleDeletePoll,

    // Document handlers
    handleAddDocument,
    handleUpdateDocument,
    handleDeleteDocument,

    // Sales log handlers
    handleAddSaleLog,

    // Helper functions
    getAllShowsData,
    getUpcomingShows,
    getTotalInventoryValue,
    getUnvotedPollsCount,
  };
}

export default useAppData;